import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { HttpEventType } from '@angular/common/http';
import { filter, map, merge, switchMap, take, tap } from 'rxjs';
import {
  AbilityService,
  BtnLoaderDirective,
  DocPreviewComponent,
  FileUploadService,
  NgbModalService,
  SpinnerService,
  SvgIconsComponent
} from '@zayan-erp/shared';
import { DealAmendmentUploadDraft } from '../../../../models/deal-document.models';
import { SalesApsService } from '../../../../services/sales-aps.service';
import { DealDocumentsService } from '@zayan-erp/sales';

@Component({
  selector: 'lib-amendment-document',
  imports: [DocPreviewComponent, SvgIconsComponent, BtnLoaderDirective],
  standalone: true,
  templateUrl: './amendment-document.html'
})
export class AmendmentDocument {
  readonly #modalService = inject(NgbModal);
  readonly #ngbModalService = inject(NgbModalService);
  readonly #salesApsService = inject(SalesApsService);
  readonly #toastrService = inject(ToastrService);
  readonly #fileUploadService = inject(FileUploadService);
  readonly #dealDocService = inject(DealDocumentsService);
  readonly #abilityService = inject(AbilityService);

  readonly canWriteDeals = computed(() => this.#abilityService.ability.can('write-sales-deals', 'deals'));
  readonly generateDocumentButtonId = 'generate-document-amendment';

  dealId = input.required<string>();
  sequenceNumber = input<number>(1);

  refreshRequested = output<void>();
  documentGenerated = output<void>();

  @ViewChild('documentPreviewModal') documentPreviewModal!: TemplateRef<unknown>;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private readonly _selectedDocument = signal<{ name: string; url: string } | null>(null);
  uploadedDocument = signal<DealAmendmentUploadDraft | null>(null);

  selectedDocument = computed(() => this._selectedDocument());
  hasUploadedDocument = computed(() => this.uploadedDocument() !== null);
  canGenerate = computed(() => Boolean(this.uploadedDocument()?.documentId));

  private readonly restoreDraftEffect = effect(
    () => {
      const dealId = this.dealId();
      if (!dealId) {
        this.uploadedDocument.set(null);
        return;
      }
      this.uploadedDocument.set(this.#dealDocService.getDealAmendmentUploadDraft(dealId));
    },
    { allowSignalWrites: true }
  );

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.uploadDocument(file);
      input.value = '';
    }
  }

  uploadDocument(file: File): void {
    if (!this.canWriteDeals()) return;
    if (!file) {
      this.#toastrService.warning('Please select a file to upload');
      return;
    }

    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!['doc', 'docx'].includes(extension)) {
      this.#toastrService.warning('Only DOC, and DOCX files are allowed');
      return;
    }

    // this.#spinnerService.handleSpinnerStart();
    this.#fileUploadService
      .getPrivateAWSSignedUrl(file)
      .pipe(
        switchMap(awsData =>
          this.#fileUploadService.uploadFileToS3WithProgress(awsData).pipe(
            filter(event => event.type === HttpEventType.Response),
            take(1),
            map(() => awsData)
          )
        ),
        switchMap(awsData =>
          this.#fileUploadService
            .getPresignedUrlFromS3Key(awsData.s3Key, file.name)
            .pipe(map(directUrl => ({ ...awsData, directUrl })))
        ),
        tap(awsData => {
          const draft: DealAmendmentUploadDraft = {
            documentId: awsData.documentId,
            documentName: file.name,
            s3Key: awsData.s3Key,
            directUrl: awsData.directUrl
          };
          this.persistDraft(draft);
        })
      )
      .subscribe({
        // error: () => this.#toastrService.error('Failed to upload document')
      });
    // .add(() => this.#spinnerService.handleSpinnerStop());
  }

  removeUploadedDocument(): void {
    if (!this.canWriteDeals()) return;
    const dealId = this.dealId();
    if (dealId) {
      this.#dealDocService.clearDealAmendmentUploadDraft(dealId);
    }
    this.uploadedDocument.set(null);
  }

  openUploadedDocumentPreview(): void {
    const uploaded = this.uploadedDocument();
    if (!uploaded) {
      return;
    }

    const previewUrl = uploaded.directUrl;
    if (previewUrl) {
      this.openPreviewModal(uploaded.documentName, previewUrl);
      return;
    }

    // this.#spinnerService.handleSpinnerStart();
    this.#salesApsService.getDocumentFileUrl(uploaded.documentId).subscribe({
      next: fileUrl => {
        if (!fileUrl) {
          this.#toastrService.error('Document preview is unavailable');
          return;
        }
        this.openPreviewModal(uploaded.documentName, fileUrl);
      },
      error: () => this.#toastrService.error('Document preview is unavailable')
    });
    // .add(() => this.#spinnerService.handleSpinnerStop());
  }

  generateAmendmentDocument(): void {
    if (!this.canWriteDeals()) return;
    const dealId = this.dealId();
    const uploaded = this.uploadedDocument();
    if (!dealId || !uploaded?.documentId) {
      this.#toastrService.warning('Please upload a document before generating');
      return;
    }

    const payload = {
      documentPlacements: [
        {
          templateId: null,
          s3DocumentId: uploaded.documentId,
          type: 'custom' as const
        }
      ]
    };

    this.#dealDocService.generateAmendmentDocument(dealId, payload, this.generateDocumentButtonId).subscribe({
      next: () => {
        this.#salesApsService.realoadDeal.next(null);
        this.removeUploadedDocument();
        this.documentGenerated.emit();
        this.refreshRequested.emit();
      }
    });
  }

  private persistDraft(draft: DealAmendmentUploadDraft): void {
    const dealId = this.dealId();
    if (dealId) {
      this.#dealDocService.setDealAmendmentUploadDraft(dealId, draft);
    }
    this.uploadedDocument.set(draft);
  }

  private openPreviewModal(name: string, url: string): void {
    this._selectedDocument.set({ name, url });
    const modalRef = this.#modalService.open(this.documentPreviewModal, this.#ngbModalService.fullscreenModal);
    merge(modalRef.closed, modalRef.dismissed).subscribe(() => {
      this._selectedDocument.set(null);
    });
  }
}
